import { get, mapKeys, map, concat } from "lodash";
import curryConnector from "utils/connectors";
import { ActionTypes as ReferralsActionTypes } from "app/main/referrals/reducers/referrals.reducers";

export const PATIENT_REFERRAL_WORKLIST_STATE_KEY = "patientReferralWorklist";
export const PATIENT_REFERRAL_PAGE_SIZE = 15;

const curry = fn => curryConnector(fn, PATIENT_REFERRAL_WORKLIST_STATE_KEY);

export const ActionTypes = {
  LOADING_PATIENT_REFERRAL_WORKLIST: "LOADING_PATIENT_REFERRAL_WORKLIST",
  LOADED_PATIENT_REFERRAL_WORKLIST: "LOADED_PATIENT_REFERRAL_WORKLIST",
  ERROR_LOADING_PATIENT_REFERRAL_WORKLIST: "ERROR_LOADING_PATIENT_REFERRAL_WORKLIST",

  UPDATE_PATIENT_REFERRAL_WORKLIST_SEARCH_PARAMS: "UPDATE_PATIENT_REFERRAL_WORKLIST_SEARCH_PARAMS",
  RESET_PATIENT_REFERRAL_WORKLIST_RESULTS: "RESET_PATIENT_REFERRAL_WORKLIST_RESULTS",
};

const INITIAL_STATE = {
  all: null,
  pageInfo: {
    pageNumber: 1,
    pageSize: PATIENT_REFERRAL_PAGE_SIZE,
    totalRecords: 0,
  },
  pages: { loading: false, loaded: false, error: null },
  searchParams: { name: null },
  patientReferralForWorklist: null,
};

export default (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case ActionTypes.LOADING_PATIENT_REFERRAL_WORKLIST:
      return {
        ...state,
        pages: { ...state.pages, [action.payload.pageNumber]: { loading: true, loaded: false, error: false } },
        pageInfo: { ...state.pageInfo, pageNumber: action.payload.pageNumber },
        patientReferralForWorklist: (action.payload.pageNumber === 1) ? [] : state.patientReferralForWorklist,
      };
    case ActionTypes.LOADED_PATIENT_REFERRAL_WORKLIST:
      return {
        ...state,
        searchParams: action.payload.searchParams,
        pageInfo: action.payload.pageInfo,
        all: { ...state.all, ...mapKeys(action.payload.patients, x => x.patient.patientId) },
        pages: { ...state.pages, [action.payload.pageNumber]: { loading: false, error: false, loaded: true } },
        patientReferralForWorklist: (action.payload.pageInfo.pageNumber === 1)
          ? map(action.payload.patients, x => x.patient.patientId)
          : concat(state.patientReferralForWorklist, map(action.payload.patients, x => x.patient.patientId)),
      };
    case ActionTypes.ERROR_LOADING_PATIENT_REFERRAL_WORKLIST:
      return { ...state,
        pages: { ...state.pages, [action.payload.pageNumber]: { loading: false, error: action.payload.message, loaded: false } } };
    case ReferralsActionTypes.CREATE_MANUAL_REFERRAL:
      return {
        ...state,
        all: state.all ? {
          ...state.all,
          [action.payload.patientId]: {
            ...state.all[action.payload.patientId],
            referralCount: state.all[action.payload.patientId]?.referralCount + 1,
          },
        } : state.all,
      };
    case ActionTypes.UPDATE_PATIENT_REFERRAL_WORKLIST_SEARCH_PARAMS:
      return {
        ...state,
        searchParams: { ...state.searchParams, ...action.payload },
      };
    case ActionTypes.RESET_PATIENT_REFERRAL_WORKLIST_RESULTS:
      return {
        ...state,
        searchParams: INITIAL_STATE.searchParams,
        pageInfo: INITIAL_STATE.pageInfo,
      };
    default:
      return state || INITIAL_STATE;
  }
};

export const getPatientReferralsList = curry(({ localState }) => {
  const patients = get(localState, ["all"], {});
  return map(localState.patientReferralForWorklist, key => patients[key]);
});

export const isPageLoading = curry(({ localState }, pageNumber) => {
  const number = pageNumber || get(localState?.pageInfo, ["pageNumber"], 1);
  return get(localState, ["pages", number, "loading"], false);
});

export const isPageLoaded = curry(({ localState }, pageNumber) => {
  const number = pageNumber || get(localState?.pageInfo, ["pageNumber"], 1);
  return get(localState, ["pages", number, "loaded"], false);
});

export const getPageErrorMessage = curry(({ localState }, pageNumber) => {
  const number = pageNumber || get(localState?.pageInfo, ["pageNumber"], 1);
  return get(localState, ["pages", number, "error"], null);
});

export const getPatientReferralWorklistPageInfo = curry(({ localState }) => get(localState, ["pageInfo"], null));

export const getPatientReferralWorklistSearchParams = curry(({ localState }) => get(localState, ["searchParams"], null));

export const bailOutOfLoadingPatientReferrals = curry(({ localState }, pageNumber) => {
  const { loading, total } = localState;

  if (loading || (pageNumber === 1 && total !== 0)) {
    return true;
  }
  return false;
});
